import classNames from "classnames";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { FreeMode, Navigation } from "swiper/modules";
import { Swiper, SwiperClass, SwiperProps, SwiperSlide } from "swiper/react";
import Icon from "~/components/Icon";
import ProductItem from "~/components/Items/ProductItem";
import SectionTitle from "~/components/SectionTitle";
import Text from "~/components/Text";
import { Product } from "~/services/product";
import { trackingViewListItem } from "~/services/tracking";
import styles from "./ProductsSlider.module.scss";
import React from "react";
import { AnyARecord } from "node:dns";
import { ProductItemProps } from "../Items/ProductItem/ProductItem";
import useClient from "~/hooks/useClient";

export type ProductsSliderProps = {
  title?: string;
  products?: Array<Partial<Product>>;
  loading?: boolean;
  onLoadingMore?: () => void;
  listkey: any;
  onEnter?: () => void;
  onBeforeIntersection?: (e: IntersectionObserverEntry) => void;
  hasNext?: boolean;
  onImpressions?: (products: Array<Partial<Product>>) => void;
  directLabel?: string;
  directUrl?: string;
  productItemProps?: Partial<ProductItemProps>;
  isMobile?: boolean;
} & SwiperProps;

const MAX_RECORD = Number.isNaN(Number(process.env.NEXT_PUBLIC_MAX_RECORD))
  ? 100
  : Number(process.env.NEXT_PUBLIC_MAX_RECORD);

const intiBreakpoints = {
  1024: {
    slidesPerView: 4.5,
    spaceBetween: 10,
  },
  768: {
    slidesPerView: 3.5,
    spaceBetween: 10,
  },
};

const ProductsSlider: FC<ProductsSliderProps> = ({
  title,
  products = [],
  onLoadingMore,
  hasNext = false,
  listkey,
  loading = true,
  onEnter,
  onImpressions,
  slidesPerView = 5.5 as number,
  breakpoints = intiBreakpoints,
  navigation = true,
  pagination = true,
  modules = [],
  onSlideChange,
  directLabel,
  directUrl,
  productItemProps,
  onBeforeIntersection,
  isMobile,
  ...restProps
}) => {
  const isClient = useClient();
  const [InvalidSku, setInvalidSku] = useState(new Set());

  // Use Ref to track SKUs without triggering re-renders
  const trackedSKUsRef = useRef(new Set());
  // Ref for currently visible SKUs
  const visibleSKUsRef = useRef(new Set());

  const observerRef = useRef<any>(null);

  /**
   * Handles visibility changes of IntersectionObserver entries.
   *
   * Iterates through the entries and updates the visibleSKUsRef set accordingly.
   * If an entry is intersecting, its SKU is added to the set. Otherwise, it's removed.
   *
   * @param {IntersectionObserverEntry[]} entries - An array of IntersectionObserver entries.
   * @return {void}
   */
  function handleVisibility(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const sku = entry.target.getAttribute("data-sku");
        if (sku) {
          visibleSKUsRef.current.add(sku);
        }
      } else {
        const sku = entry.target.getAttribute("data-sku");
        if (sku) {
          visibleSKUsRef.current.delete(sku);
        }
      }
    });
  }

  // Send tracked SKUs to Google Analytics
  const sendImpressions = useCallback(() => {
    if (!listkey) return;
    const skusToTrack = Array.from(visibleSKUsRef.current).filter(
      (sku) => !trackedSKUsRef.current.has(sku),
    );

    if (skusToTrack.length > 0) {
      onImpressions?.(
        products.filter((product) => skusToTrack.includes(product.sku)),
      );
      trackingViewListItem({
        listKey: listkey,
        products: products.filter((product) =>
          skusToTrack.includes(product.sku),
        ),
      });
      skusToTrack.forEach((sku) => trackedSKUsRef.current.add(sku));
    }
  }, [products, visibleSKUsRef.current, listkey, onImpressions]);

  const handleOnSlideChange = useCallback(
    (swiper: SwiperClass) => {
      if (!!loading) return;
      if (products.length >= MAX_RECORD) {
        return;
      }
      sendImpressions();
    },
    [products],
  );

  const handleRealIndexChange = useCallback(
    (swiper: SwiperClass) => {
      if (!hasNext) return;
      if (!!loading) return;
      // if (products.length > MAX_RECORD) return;
      const currentIndex = swiper?.realIndex ?? 0;
      const silderPerView =
        typeof swiper?.params?.slidesPerView === "number"
          ? swiper?.params?.slidesPerView
          : 5;

      const remainingProducts = products.length - currentIndex;

      if (remainingProducts <= silderPerView) {
        return onLoadingMore?.();
      }
    },
    [products],
  );

  /**
   * Initializes an IntersectionObserver instance to track visibility of product items.
   *
   * Disconnects any existing observer, sets up a new observer with a threshold of 0.5,
   * and starts observing product items that have not been tracked yet.
   *
   * @return {void}
   */

  const initializeObserver = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    observerRef.current = new IntersectionObserver(handleVisibility, {
      threshold: 0.1,
    });
    const productItemElement =
      document.querySelectorAll(`.product-item-${listkey}`) || [];
    productItemElement?.forEach((item) => {
      const sku = item.getAttribute("data-sku");
      if (sku && !trackedSKUsRef.current.has(sku)) {
        // Kiểm tra xem SKU đã được track hay chưa
        observerRef.current.observe(item);
      }
    });
  };

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [products]);

  // const swiperRef = useRef<any>(null);

  // check invisible product-list
  useEffect(() => {
    const ProductsSliderElement = document.getElementById(listkey + "");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // onEnter?.();
            onBeforeIntersection?.(entry);
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.25,
        rootMargin: "0px 0px 200px 0px",
      },
    );
    const observer2 = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onEnter?.();
            observer2.disconnect();
          }
        });
      },
      {
        threshold: 0, // when viewport is 0% visible
        rootMargin: "0px 0px 10px 0px", // margin around the root
      },
    );

    ProductsSliderElement && observer.observe(ProductsSliderElement);
    ProductsSliderElement && observer2.observe(ProductsSliderElement);

    return () => {
      ProductsSliderElement && observer.unobserve(ProductsSliderElement);
      ProductsSliderElement && observer2.unobserve(ProductsSliderElement);
    };
  }, []);
  useEffect(() => {
    if (products?.length > 0) {
      initializeObserver();
    }
  }, [products?.length]);

  const handleAfterInit = useCallback(
    (swiper: SwiperClass) => {
      if (!products.length || !!swiper.destroyed) return;
      const sliderPerView = swiper?.params?.slidesPerView ?? 5;
      const productOnView = [...products].slice(0, sliderPerView as number);
      productOnView?.map(({ sku }) => {
        visibleSKUsRef.current.add(sku);
      });

      sendImpressions();
    },
    [sendImpressions],
  );

  const handleOnComplete = (data: Product, clientOptions: any) => {
    if (!data) {
      const sku = clientOptions?.variables.sku;
      setInvalidSku((prev: any) => {
        prev.add(clientOptions?.variables?.sku);
        return new Set(prev);
      }); // Cập nhật danh sách các item hợp lệ
    }
  };
  return (
    <div className="product-slider min-h-[45px] " id={listkey + ""}>
      {(!!products.length || !!loading) && (
        <React.Fragment>
          {!!title && (
            <SectionTitle
              text={title}
              action={
                directUrl ? (
                  <a
                    href={directUrl}
                    className="flex items-center gap-1 text-primary"
                  >
                    <Text type="title-3">{directLabel}</Text>
                    <Icon name="arrow-up-right" />
                  </a>
                ) : undefined
              }
            />
          )}
          {!!loading &&
            !products.length &&
            skeletonProductList({
              slidesPerView: 2.5,
              lgSlidesPerView: 5.5,
              productItemProps,
            })}
          {!!products.length && (
            <Swiper
              {...restProps}
              slidesPerView={2.5}
              spaceBetween={10}
              onAfterInit={handleAfterInit}
              navigation={
                products.length > (slidesPerView as number)
                  ? {
                      enabled: true,
                      hideOnClick: true,
                    }
                  : undefined
              }
              cssMode={true}
              freeMode={true}
              onRealIndexChange={handleRealIndexChange}
              watchSlidesProgress={true}
              breakpoints={{
                ...breakpoints,
                1024: {
                  ...breakpoints[1024],
                  slidesPerView: slidesPerView,
                  cssMode: false,
                },
              }}
              onSlideChange={handleOnSlideChange}
              modules={[Navigation, FreeMode, ...modules]}
              wrapperClass={classNames(
                "items-stretch",
                styles.productSliderWrapper,
                {
                  "grid-rows-[1fr_0_0_0_0_0] grid grid-cols-[repeat(5,_1fr)_0.5fr]":
                    !isClient && !isMobile,
                  "grid-rows-[1fr_0_0_0_0_0] grid grid-cols-[repeat(2,_1fr)_0.5fr]":
                    !isClient && isMobile,
                },
              )}
              className={classNames({
                "pr-[45px] overflow-hidden": !isClient,
              })}
            >
              {[...products]?.map(
                (
                  data: Partial<Product & { loading?: boolean }>,
                  index: number,
                ) => {
                  if (InvalidSku.has(data.sku)) return null;
                  return (
                    <SwiperSlide
                      key={data.id + "_" + index}
                      className={classNames(styles.productItem, {
                        hidden: InvalidSku.has(data.sku),
                        "mr-[10px] max-w-[230px]": !isClient,
                      })}
                      data-slide-id={data.id}
                    >
                      <div
                        className={`h-full product-item-${listkey}`}
                        data-sku={data.sku}
                        key={data.id}
                      >
                        <ProductItem
                          data={data}
                          params={{ b: listkey, p: index + 1 }}
                          onCompleted={handleOnComplete}
                          {...productItemProps}
                        />
                      </div>
                    </SwiperSlide>
                  );
                },
              )}
              {!!hasNext ? (
                <>
                  <SwiperSlide key={1} className="">
                    <ProductItem data={{}} loading={true} key={1} />
                  </SwiperSlide>
                  <SwiperSlide key={2} className="">
                    <ProductItem data={{}} loading={true} key={2} />
                  </SwiperSlide>
                  <SwiperSlide key={3} className="">
                    <ProductItem data={{}} loading={true} key={3} />
                  </SwiperSlide>
                </>
              ) : null}
            </Swiper>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default ProductsSlider;

export const skeletonProductList = ({
  slidesPerView,
  lgSlidesPerView,
  productItemProps = {},
}: {
  slidesPerView: number;
  lgSlidesPerView: number;
  productItemProps: any;
}) => {
  return (
    <div className="flex overflow-hidden gap-[10px] w-full">
      {/* Tailwind classes are dynamically applied based on the calculated widths */}
      {Array.from(
        { length: Math.ceil(lgSlidesPerView) },
        (item: number, index: number) => {
          return (
            <div
              key={index + "_loading"}
              className={classNames(
                `flex-none w-1/3 lg:w-1/6  max-w-1/3 md:max-w-1/6`,
              )}
            >
              <ProductItem data={{}} loading={true} {...productItemProps} />
            </div>
          );
        },
      )}
    </div>
  );
};
